package org.teddapp.project.ui.portfolio

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import com.tedd.db.TeddDatabase
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import org.jetbrains.compose.resources.ExperimentalResourceApi
import org.jetbrains.compose.resources.decodeToImageBitmap
import org.teddapp.project.db.nowMillis
import org.teddapp.project.portfolio.PieceImage
import org.teddapp.project.sync.TeddFiles
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.admin.PickedImage
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid

/**
 * Les images d'une pièce choisies sur l'appareil. Comme pour les notes, une image est un
 * fichier joint : une ligne `attachment` synchronisée, ses octets gardés sur l'appareil
 * jusqu'à leur envoi, et une entrée `file:<id>` dans la liste d'images de la pièce. Les
 * images par adresse web restent possibles à côté.
 */
internal object PieceImages {
    private const val PREFIX = "file:"

    fun isFile(url: String) = url.startsWith(PREFIX)

    fun fileIdOf(url: String): String? = url.removePrefix(PREFIX).takeIf { isFile(url) && it.isNotBlank() }

    fun ref(id: String) = PREFIX + id

    private fun fileName(type: String) = "image." + when (type) {
        "image/png" -> "png"
        "image/webp" -> "webp"
        else -> "jpg"
    }

    /** Range l'image choisie pour la pièce [pieceId] et rend l'entrée à mettre dans sa liste. */
    @OptIn(ExperimentalUuidApi::class)
    suspend fun store(db: TeddDatabase, pieceId: String, image: PickedImage): PieceImage = withContext(Dispatchers.Default) {
        val id = Uuid.random().toString()
        val now = nowMillis()
        db.transaction {
            db.fileBlobQueries.put(id, image.bytes, now)
            db.attachmentQueries.insert(
                id, "portfolio_piece", pieceId, fileName(image.contentType), image.contentType,
                image.bytes.size.toLong(), null, null, null, null, "pending", 0L, now, now,
            )
        }
        PieceImage(ref(id), "")
    }

    /** Retire les fichiers de la pièce que sa liste d'images ne cite plus. */
    suspend fun pruneOrphans(db: TeddDatabase, pieceId: String, images: List<PieceImage>) = withContext(Dispatchers.Default) {
        val cited = images.mapNotNullTo(mutableSetOf()) { fileIdOf(it.url) }
        val now = nowMillis()
        db.attachmentQueries.selectByOwner("portfolio_piece", pieceId).executeAsList()
            .filter { it.id !in cited }
            .forEach { a ->
                db.transaction {
                    db.attachmentQueries.softDelete(now, now, a.id)
                    db.fileBlobQueries.remove(a.id)
                }
            }
    }
}

/**
 * Une image de pièce : depuis ses octets quand c'est un fichier de l'appareil, par le
 * réseau quand c'est une adresse web.
 */
@OptIn(ExperimentalResourceApi::class)
@Composable
internal fun PieceImageView(db: TeddDatabase, image: PieceImage, modifier: Modifier = Modifier) {
    val shape = RoundedCornerShape(TeddTokens.RMd)
    val base = modifier.background(TeddTokens.SurfaceSunken, shape)
    val fileId = PieceImages.fileIdOf(image.url)
    if (fileId == null) {
        AsyncImage(model = image.url, contentDescription = image.caption.ifBlank { null }, contentScale = ContentScale.Crop, modifier = base)
        return
    }
    var loading by remember(fileId) { mutableStateOf(true) }
    var bitmap by remember(fileId) { mutableStateOf<ImageBitmap?>(null) }
    LaunchedEffect(fileId) {
        bitmap = withContext(Dispatchers.Default) { TeddFiles.bytes(db, fileId)?.let { runCatching { it.decodeToImageBitmap() }.getOrNull() } }
        loading = false
    }
    val b = bitmap
    if (b != null) {
        Image(bitmap = b, contentDescription = image.caption.ifBlank { null }, contentScale = ContentScale.Crop, modifier = base)
    } else {
        Box(base.border(1.dp, TeddTokens.Line, shape), contentAlignment = Alignment.Center) {
            Text(if (loading) "Chargement…" else "Image indisponible hors ligne", color = TeddTokens.InkSubtle, style = TeddType.Caption)
        }
    }
}
